'use client';
import {styled} from '@mui/material/styles';
import {NavArrow, PaginationNumber} from '../primitives';

export interface ListingPaginationProps {
  page: number;
  totalPages: number;
  onChange: (page: number) => void;
}

// `[MASTER] Pagination`, desktop node 111:3185 / mobile node 111:3234 — both
// breakpoints share the same structure: [Carousel Arrow] [Pagination number]
// x<=3 [Carousel Arrow]. Navigation is strictly sequential at the edges (prev/
// next disable at the ends) with direct jumps available for the up-to-3
// numbers shown (current page, clamped to the range).
const Root = styled('nav')(({theme}) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(1.5),
}));

// Up to 3 page numbers centered on the current page, clamped so the window
// never runs past either end of the range.
function getPageWindow(page: number, totalPages: number): number[] {
  const windowSize = Math.min(3, totalPages);
  const start = Math.min(Math.max(1, page - 1), totalPages - windowSize + 1);
  return Array.from({length: windowSize}, (_, index) => start + index);
}

export function ListingPagination({page, totalPages, onChange}: ListingPaginationProps) {
  if (totalPages <= 1) {
    return null;
  }

  return (
    <Root>
      <NavArrow
        direction="prev"
        onClick={() => {
          onChange(page - 1);
        }}
        disabled={page <= 1}
      />
      {getPageWindow(page, totalPages).map(pageNumber => (
        <PaginationNumber
          key={pageNumber}
          page={pageNumber}
          selected={pageNumber === page}
          onClick={() => {
            onChange(pageNumber);
          }}
        />
      ))}
      <NavArrow
        direction="next"
        onClick={() => {
          onChange(page + 1);
        }}
        disabled={page >= totalPages}
      />
    </Root>
  );
}

ListingPagination.displayName = 'ListingPagination';
